import React, { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client";
import SppWidget from "../../../common/spp/SppWidget";
import { TargetTypeEnum } from "../../../../classes/enums/SppEnum";
import { GET_MEASUREMENT_CONFIGS } from "../../../../queries/measuramentConfigQueries";
import { useSettings } from "../../hooks/useSettings";

const GENERAL_CATEGORIES = [
  "general",
  "pelvisParameters",
  "lumbarLordosisParameters",
  "thoracicKyphosisParameters",
  "sagittalAlignmentParameters",
  "cervicalParameters",
  "coronalParameters",
];

const EMPTY_CUSTOM_CONFIGS = {
  advancedMeasurements: [],
  distance: [],
  angle: [],
};

export const KeyParameterSelector = () => {
  const {
    keyParameters,
    updateKeyParameters,
    preferencesLoading,
    userPreferences,
  } = useSettings();

  const { data, loading, error } = useQuery(GET_MEASUREMENT_CONFIGS, {
    fetchPolicy: "cache-first",
  });

  const measurementConfigs = useMemo(() => {
    if (!data?.measurementConfigs) {
      return null;
    }

    const grouped = GENERAL_CATEGORIES.reduce((acc, category) => {
      acc[category] = [];
      return acc;
    }, {});

    data.measurementConfigs.forEach(config => {
      if (!GENERAL_CATEGORIES.includes(config.category)) {
        return;
      }

      grouped[config.category].push({
        name: config.fullName,
        fullName: config.fullName,
        description: config.description,
        shortName: config.shortName,
        defaultValue: "-",
        unit: config.unit,
        key: config.unique_identifier,
        redRegions: config.range?.red ?? [],
        yellowRegions: config.range?.yellow ?? [],
        greenRegions: config.range?.green ?? [],
        nature: config.nature,
        pythonClass: config.pythonClass,
      });
    });

    return grouped;
  }, [data]);

  const activeParameters = useMemo(() => {
    if (!measurementConfigs) {
      return {};
    }

    const active = {};
    Object.values(measurementConfigs).forEach(categoryConfigs => {
      categoryConfigs.forEach(param => {
        active[param.key] = true;
      });
    });

    return active;
  }, [measurementConfigs]);

  const updateParamsWithValues = useCallback(
    params =>
      params.map(param => ({
        ...param,
        originalValue: null,
        value: null,
      })),
    []
  );

  const handleFavoritesChange = useCallback(
    favorites => {
      const normalizedFavorites = favorites
        .map(key => Number(key))
        .filter(Number.isFinite);

      const existing = new Set(
        (keyParameters || [])
          .map(key => Number(key))
          .filter(Number.isFinite)
      );

      const hasChanged =
        normalizedFavorites.length !== existing.size ||
        normalizedFavorites.some(value => !existing.has(value));

      if (!hasChanged) {
        return;
      }

      updateKeyParameters(normalizedFavorites);
    },
    [updateKeyParameters, keyParameters]
  );

  if (loading || preferencesLoading || !userPreferences) {
    return (
      <div className="settings-key-parameters-loading">
        Loading parameters...
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-key-parameters-error">
        Unable to load parameter configuration.
      </div>
    );
  }

  if (!measurementConfigs) {
    return null;
  }

  return (
    <SppWidget
      updateParamsWithValues={updateParamsWithValues}
      onSppSelected={() => {}}
      onSppSelectedCustom={() => {}}
      removeCustomParameter={() => {}}
      activeParameters={activeParameters}
      measurementConfigs={measurementConfigs}
      customMeasuramentConfigs={EMPTY_CUSTOM_CONFIGS}
      isOctView={false}
      selectedTargetType={TargetTypeEnum.GAP}
      showCoronalParameters={true}
      digitalTwinId={null}
      initialKeyParameters={keyParameters}
      onFavoritesChange={handleFavoritesChange}
      isSettingsMode
    />
  );
};

export default KeyParameterSelector;
